import { Link, useNavigate } from '@tanstack/react-router';
import { AvatarDisc, Button, EmptyState, LoadingState, MonoLabel, Row, SectionHeader, Star } from '@/component';
import { useFavourites, useFriendActions, useProfile, type Profile } from '@/hook';
import { cn } from '@/lib/cn';
import { formatDayLabel } from '@/lib/time';
import { RelationAction } from './RelationAction';
import { personName } from './personName';

const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

/** "64 songs sung · 7 tracks created · 3 mutual friends · friends since
 * March". Mutual friends appear once there are any; the year is added once
 * the friendship is older than this one. */
export function profileStats(
  profile: Pick<Profile, 'songsSung' | 'tracksCreated' | 'mutualFriendCount' | 'relation'>,
  now = new Date()
): string {
  const parts = [plural(profile.songsSung, 'song') + ' sung', plural(profile.tracksCreated, 'track') + ' created'];
  if (profile.mutualFriendCount > 0) parts.push(plural(profile.mutualFriendCount, 'mutual friend'));
  if (profile.relation.state === 'friends') {
    const since = new Date(profile.relation.since);
    const month = since.toLocaleDateString('en-GB', { month: 'long' });
    parts.push(`friends since ${since.getFullYear() === now.getFullYear() ? month : `${month} ${since.getFullYear()}`}`);
  }
  return parts.join(' · ');
}

/** /u/:handle: who someone is and what they sing. Mutual friends are named
 * only to a friend. Block asks first, then leaves for /friends, where the
 * Blocked list is the way back. */
export function ProfilePage({ handle }: { handle: string }) {
  const profile = useProfile(handle);
  const { favourites, toggle } = useFavourites();
  const { block } = useFriendActions();
  const navigate = useNavigate();

  async function confirmBlock(person: Pick<Profile, 'userId' | 'displayName' | 'handle'>) {
    const ok = window.confirm(`Block ${personName(person)}? Neither of you will find or see the other. You can unblock from Friends.`);
    if (!ok) return;
    await block(person.userId);
    await navigate({ to: '/friends' });
  }

  return (
    <div className="pt-10">
      <Link to="/friends" className="hover:text-ink">
        <MonoLabel tone="mute">← Friends</MonoLabel>
      </Link>
      {profile === undefined && <LoadingState />}
      {profile === null && <EmptyState title={`Nobody is @${handle}.`} body="Check the handle and try again." />}
      {profile && (
        <>
          <div className="mt-7 flex flex-wrap items-center gap-[22px]">
            <AvatarDisc user={profile} size={88} />
            <div className="min-w-0">
              <MonoLabel size="md" className="block">
                @{profile.handle}
              </MonoLabel>
              <h1 className="mt-2 text-[38px] font-semibold leading-[1.05] tracking-[-0.03em]">{personName(profile)}</h1>
              <p className="mt-1.5 text-[15px] text-mute">{profileStats(profile)}</p>
              {profile.mutualFriends && profile.mutualFriends.length > 0 && (
                <p className="mt-1 text-[13px] text-mute">
                  Mutual friends:{' '}
                  {profile.mutualFriends.map((friend, index) => (
                    <span key={friend.userId}>
                      {index > 0 && ', '}
                      {friend.handle ? (
                        <Link to="/u/$handle" params={{ handle: friend.handle }} className="text-ink hover:text-acc">
                          {personName(friend)}
                        </Link>
                      ) : (
                        personName(friend)
                      )}
                    </span>
                  ))}
                </p>
              )}
            </div>
            <div className="ml-auto flex items-center gap-[10px]">
              <RelationAction userId={profile.userId} relation={profile.relation} size="md" />
              {profile.relation.state !== 'self' && (
                <Button variant="text" quiet onClick={() => void confirmBlock(profile)}>
                  Block
                </Button>
              )}
            </div>
          </div>

          <div className="mt-[52px] grid grid-cols-1 items-start gap-14 md:grid-cols-2">
            <section>
              <SectionHeader title="Recently sung" />
              {profile.recentlySung.length === 0 && <p className="py-6 text-sm text-mute">Nothing sung yet.</p>}
              {profile.recentlySung.map((song) => (
                <Row key={song.historyId}>
                  <div className="min-w-0 flex-1">
                    <Link to="/tracks/$trackId" params={{ trackId: song.trackId }} className="block truncate text-[15px] font-semibold tracking-[-0.01em] hover:text-acc">
                      {song.title}
                    </Link>
                    <div className="mt-0.5 text-[13px] text-mute">{song.artist}</div>
                  </div>
                  <MonoLabel className="shrink-0 tracking-[0.1em]">{formatDayLabel(song.sangAt)}</MonoLabel>
                  <Star on={favourites.has(song.trackId)} onToggle={() => toggle(song.trackId)} />
                </Row>
              ))}
            </section>
            <section>
              <SectionHeader title="Their signature songs" />
              {profile.signatureSongs.length === 0 && <p className="py-6 text-sm text-mute">No signature songs yet.</p>}
              {profile.signatureSongs.map((song) => (
                <Row key={song.trackId}>
                  <span className={cn('w-[22px] shrink-0 text-[17px] font-semibold tabular-nums', song.rank === 1 ? 'text-acc' : 'text-faint')}>
                    {song.rank}
                  </span>
                  <div className="min-w-0 flex-1">
                    <Link to="/tracks/$trackId" params={{ trackId: song.trackId }} className="block truncate text-[15px] font-semibold tracking-[-0.01em] hover:text-acc">
                      {song.title}
                    </Link>
                    <div className="mt-0.5 text-[13px] text-mute">{song.artist}</div>
                  </div>
                  <span className="shrink-0 font-mono text-xs tabular-nums text-mute">× {song.times}</span>
                </Row>
              ))}
            </section>
          </div>
        </>
      )}
    </div>
  );
}
